import {
  AVAILABLE_SOURCE_TYPES,
  createKnowledgeSourceSchema,
  createTextDocumentSchema,
  fileTypeFromName,
  KNOWLEDGE_SOURCE_TYPES,
  minimumPlanFor,
  PLAN_LABEL,
  refreshAllowed,
  URL_REFRESH,
  urlSourceConfigSchema,
  apiSourceConfigSchema,
  type DocumentStatus,
  type EntitlementsView,
  type KnowledgeDocument,
  type KnowledgeSource,
  type KnowledgeSourceType,
  type UrlRefresh,
} from '@agents/shared';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useRef, useState, type DragEvent, type FormEvent } from 'react';
import {
  ApiConnectorFields,
  apiErrors,
  apiFormFrom,
  toConfig,
  type ApiForm,
} from '../components/api-connector';
import { Icon, type IconName } from '../components/icons';
import { useCan } from '../components/shell';
import {
  Alert,
  BackLink,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  FormDialog,
  OverflowMenu,
  PageHeader,
  problemMessage,
  SkeletonRows,
  StatusPill,
  TableSkeleton,
  useToast,
} from '../components/ui';
import { ApiProblem } from '../lib/api';
import { useDocumentTitle } from '../lib/document-title';
import { clearError } from '../lib/forms';
import { dateTime, fileSize, relativeTime } from '../lib/format';
import {
  isDocumentPending,
  useAddKnowledgeText,
  useCreateKnowledgeSource,
  useDeleteKnowledgeDocument,
  useDeleteKnowledgeSource,
  useEntitlements,
  useKnowledgeSource,
  useKnowledgeSources,
  useKnowledgeUsage,
  useRetryKnowledgeDocument,
  useSyncKnowledgeSource,
  useUpdateKnowledgeSource,
  useUploadKnowledgeFile,
} from '../lib/queries';

const sourceRoute = getRouteApi('/_app/conocimiento/$sourceId');

const SOURCE_TYPE: Record<KnowledgeSourceType, { label: string; icon: IconName; text: string }> = {
  text: {
    label: 'Texto',
    icon: 'edit',
    text: 'Políticas, respuestas frecuentes o notas escritas aquí mismo.',
  },
  file: {
    label: 'Archivos',
    icon: 'file',
    text: 'Documentos PDF, Word, Excel y otros formatos, según tu plan.',
  },
  url: {
    label: 'Sitio web',
    icon: 'globe',
    text: 'Páginas públicas de tu sitio, actualizadas periódicamente.',
  },
  api: { label: 'API', icon: 'database', text: 'Datos de tus sistemas, sincronizados según tu plan.' },
  mcp: { label: 'Servidor MCP', icon: 'plug', text: 'Consultas en vivo a un servidor MCP de tu empresa.' },
};

const DOCUMENT_STATUS: Record<
  DocumentStatus,
  { label: string; tone?: 'success' | 'warning' | 'danger' | 'info' }
> = {
  uploaded: { label: 'Cargando', tone: 'info' },
  scanning: { label: 'Analizando seguridad', tone: 'info' },
  processing: { label: 'Procesando', tone: 'info' },
  ready: { label: 'Listo', tone: 'success' },
  failed: { label: 'Falló', tone: 'danger' },
  quarantined: { label: 'Bloqueado', tone: 'danger' },
  disabled: { label: 'Eliminado' },
};

const FILE_LABEL: Record<string, string> = {
  pdf: 'PDF',
  docx: 'Word',
  xlsx: 'Excel',
  csv: 'CSV',
  txt: 'TXT',
  md: 'Markdown',
};

function DocumentStatusPill({ doc }: { doc: KnowledgeDocument }) {
  const s = DOCUMENT_STATUS[doc.status];
  return (
    <StatusPill tone={s.tone} live={isDocumentPending(doc)}>
      {s.label}
    </StatusPill>
  );
}

/** Why a source type cannot be chosen, or null when it can. */
function lockReason(type: KnowledgeSourceType, ent: EntitlementsView | undefined): string | null {
  if (!ent) return null;
  const key = `knowledge.sources.${type}` as const;
  if (!ent.values[key]) {
    const min = minimumPlanFor(ent.plans, (v) => v[key]);
    return min ? `Disponible desde ${PLAN_LABEL[min]}` : 'No incluido en tu plan';
  }
  if (!(AVAILABLE_SOURCE_TYPES as readonly string[]).includes(type)) return 'Próximamente';
  return null;
}

/* ------------------------------------------------------------------ Usage */

function Meter({
  label,
  used,
  limit,
  format,
}: {
  label: string;
  used: number;
  limit: number;
  format: (n: number) => string;
}) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const tone = pct >= 90 ? 'danger' : pct >= 75 ? 'warning' : undefined;
  return (
    <div className="meter" data-tone={tone}>
      <div className="meter-head">
        <span>{label}</span>
        <span className="mono">
          {format(used)} <span className="muted">de {format(limit)}</span>
        </span>
      </div>
      <div
        className="meter-bar"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function UsagePanel() {
  const usage = useKnowledgeUsage();
  if (!usage.data) return null;
  const u = usage.data;
  const count = (n: number) => n.toLocaleString('es-CO');
  return (
    <section className="usage-panel" aria-label="Uso del plan">
      <Meter label="Almacenamiento" used={u.storageBytes} limit={u.storageLimitBytes} format={fileSize} />
      <Meter label="Documentos" used={u.documents} limit={u.documentLimit} format={count} />
      <Meter
        label="Procesado este mes"
        used={u.processedBytesThisMonth}
        limit={u.processedLimitBytes}
        format={fileSize}
      />
    </section>
  );
}

/* ------------------------------------------------------------------ New source */

const REFRESH_OPTION: Record<UrlRefresh, { label: string; text: string }> = {
  manual: { label: 'Manual', text: 'Solo cuando pulses "Sincronizar ahora".' },
  weekly: { label: 'Semanal', text: 'Se vuelve a leer una vez por semana.' },
  daily: { label: 'Diaria', text: 'Se vuelve a leer cada día.' },
};

/** Pages cap and refresh frequency, limited by the plan. */
function UrlSettingsFields({
  ent,
  maxPages,
  refresh,
  errors,
  onChange,
}: {
  ent: EntitlementsView | undefined;
  maxPages: string;
  refresh: UrlRefresh;
  errors: Record<string, string>;
  onChange: (patch: { maxPages?: string; refresh?: UrlRefresh }) => void;
}) {
  const planPages = ent?.values['knowledge.url.maxPagesPerSource'] ?? 0;
  const planRefresh = ent?.values['knowledge.url.refresh'] ?? 'none';
  return (
    <>
      <Field
        label="Máximo de páginas"
        error={errors['url.maxPages']}
        help={`Tu plan permite hasta ${planPages.toLocaleString('es-CO')} páginas por sitio.`}
      >
        {(p) => (
          <input
            {...p}
            className="input"
            type="number"
            min={1}
            max={planPages}
            value={maxPages}
            onChange={(e) => onChange({ maxPages: e.target.value })}
          />
        )}
      </Field>
      <fieldset className="choice-group">
        <legend>Actualización</legend>
        {URL_REFRESH.map((r) => {
          const allowed = refreshAllowed(r, planRefresh);
          const min =
            !allowed && ent
              ? minimumPlanFor(ent.plans, (v) => refreshAllowed(r, v['knowledge.url.refresh']))
              : null;
          return (
            <label key={r} className="choice" data-disabled={allowed ? undefined : true}>
              <input
                type="radio"
                name="refresh"
                value={r}
                disabled={!allowed}
                checked={refresh === r}
                onChange={() => onChange({ refresh: r })}
              />
              <span>
                <strong>{REFRESH_OPTION[r].label}</strong>
                <small>{REFRESH_OPTION[r].text}</small>
              </span>
              {!allowed ? (
                <span className="chip">{min ? `Disponible desde ${PLAN_LABEL[min]}` : 'No incluido'}</span>
              ) : (
                <span />
              )}
            </label>
          );
        })}
      </fieldset>
    </>
  );
}

function NewSourceDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const ent = useEntitlements();
  const create = useCreateKnowledgeSource();
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [type, setType] = useState<KnowledgeSourceType>('text');
  const [startUrl, setStartUrl] = useState('');
  const [maxPages, setMaxPages] = useState('');
  const [refresh, setRefresh] = useState<UrlRefresh>('manual');
  const [apiForm, setApiForm] = useState<ApiForm>(() => apiFormFrom());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const planPages = ent.data?.values['knowledge.url.maxPagesPerSource'] ?? 0;

  const close = (next: boolean) => {
    if (!next) {
      setName('');
      setType('text');
      setStartUrl('');
      setMaxPages('');
      setRefresh('manual');
      setApiForm(apiFormFrom());
      setErrors({});
    }
    onOpenChange(next);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const url =
      type === 'url'
        ? { startUrl, maxPages: Number(maxPages || Math.min(50, planPages)), refresh }
        : undefined;
    const api = type === 'api' ? toConfig(apiForm) : undefined;
    const apiSecret =
      type === 'api' && apiForm.authType !== 'none' && apiForm.secret ? apiForm.secret : undefined;
    const parsed = createKnowledgeSourceSchema.safeParse({ name, type, url, api, apiSecret });
    if (!parsed.success) {
      setErrors({
        ...Object.fromEntries(parsed.error.issues.map((i) => [i.path.join('.'), i.message])),
        ...apiErrors(parsed.error.issues),
      });
      return;
    }
    try {
      const source = await create.mutateAsync(parsed.data);
      toast(`Fuente "${source.name}" creada`);
      close(false);
      void navigate({ to: '/conocimiento/$sourceId', params: { sourceId: source.id } });
    } catch (err) {
      if (err instanceof ApiProblem) {
        const fields = err.fieldErrors();
        const issues = Object.entries(fields).map(([k, message]) => ({ path: k.split('.'), message }));
        setErrors(
          Object.keys(fields).length
            ? { ...fields, ...apiErrors(issues) }
            : { type: err.problem.detail ?? err.problem.title },
        );
      }
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={close}
      size={type === 'url' || type === 'api' ? 'lg' : undefined}
      title="Agregar fuente de conocimiento"
      description="Una fuente agrupa documentos del mismo origen. Luego eliges qué agentes la usan."
      submitLabel="Crear fuente"
      onSubmit={submit}
      loading={create.isPending}
    >
      <Field label="Nombre" error={errors.name} help="Por ejemplo: Políticas de servicio, Catálogo 2026.">
        {(p) => (
          <input
            {...p}
            className="input"
            autoComplete="off"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrors(clearError('name'));
            }}
          />
        )}
      </Field>
      <fieldset className="choice-group">
        <legend>Tipo de fuente</legend>
        {KNOWLEDGE_SOURCE_TYPES.map((t) => {
          const reason = lockReason(t, ent.data);
          return (
            <label key={t} className="choice" data-disabled={reason ? true : undefined}>
              <input
                type="radio"
                name="type"
                value={t}
                disabled={!!reason}
                checked={type === t}
                onChange={() => {
                  setType(t);
                  setErrors(clearError('type'));
                }}
              />
              <span>
                <strong>{SOURCE_TYPE[t].label}</strong>
                <small>{SOURCE_TYPE[t].text}</small>
              </span>
              {reason ? <span className="chip">{reason}</span> : <Icon name={SOURCE_TYPE[t].icon} />}
            </label>
          );
        })}
      </fieldset>
      {type === 'url' && (
        <>
          <Field
            label="Dirección del sitio"
            error={errors['url.startUrl']}
            help="Leemos esta página y las del mismo sitio enlazadas desde ella. Respetamos robots.txt."
          >
            {(p) => (
              <input
                {...p}
                className="input"
                type="url"
                inputMode="url"
                placeholder="https://www.tuempresa.com/ayuda"
                value={startUrl}
                onChange={(e) => {
                  setStartUrl(e.target.value);
                  setErrors(clearError('url.startUrl'));
                }}
              />
            )}
          </Field>
          <UrlSettingsFields
            ent={ent.data}
            maxPages={maxPages || String(Math.min(50, planPages))}
            refresh={refresh}
            errors={errors}
            onChange={(p) => {
              if (p.maxPages !== undefined) setMaxPages(p.maxPages);
              if (p.refresh) setRefresh(p.refresh);
              setErrors(clearError('url.maxPages'));
            }}
          />
        </>
      )}
      {type === 'api' && (
        <ApiConnectorFields
          form={apiForm}
          errors={errors}
          ent={ent.data}
          onChange={(patch) => {
            setApiForm((f) => ({ ...f, ...patch }));
            setErrors({});
          }}
        />
      )}
      {errors.type && (
        <Alert tone="danger" title="No se pudo crear la fuente">
          {errors.type}
        </Alert>
      )}
    </FormDialog>
  );
}

/* ------------------------------------------------------------------ List */

export function KnowledgePage() {
  useDocumentTitle('Conocimiento');
  const can = useCan();
  const sources = useKnowledgeSources();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const manage = can('knowledge.manage');

  const createAction = manage && (
    <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
      Agregar fuente
    </Button>
  );

  return (
    <>
      <PageHeader
        title="Conocimiento"
        description="Lo que tus agentes pueden consultar para responder: textos, archivos y, según tu plan, sitios web, APIs y servidores MCP."
        actions={sources.data?.length ? createAction : undefined}
      />
      <UsagePanel />
      {sources.isPending && <TableSkeleton rows={4} cols={4} />}
      {sources.isError && <ErrorState error={sources.error} onRetry={() => void sources.refetch()} />}
      {sources.data?.length === 0 &&
        (manage ? (
          <EmptyState
            icon="book"
            title="Enséñale a tus agentes lo que sabe tu empresa"
            action={createAction}
            flow={[
              { title: 'Agregar', text: 'Escribe un texto o sube tus documentos.' },
              { title: 'Procesar', text: 'Analizamos y preparamos el contenido para consultarlo.' },
              { title: 'Asignar', text: 'Elige qué agentes pueden usar cada fuente.' },
            ]}
          >
            Los agentes responden con base en tus políticas, catálogos y manuales. Los datos de tarjetas y
            credenciales se ocultan antes de procesarse.
          </EmptyState>
        ) : (
          <EmptyState icon="book" title="Aún no hay conocimiento en este espacio">
            Cuando un administrador agregue fuentes, aparecerán aquí.
          </EmptyState>
        ))}
      {!!sources.data?.length && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Fuente</th>
                <th scope="col">Tipo</th>
                <th scope="col" className="num">
                  Documentos
                </th>
                <th scope="col" className="num" data-optional>
                  Tamaño
                </th>
                <th scope="col" data-optional>
                  Actualizada
                </th>
              </tr>
            </thead>
            <tbody>
              {sources.data.map((s: KnowledgeSource) => (
                <tr
                  key={s.id}
                  data-href
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('a, button')) return;
                    void navigate({ to: '/conocimiento/$sourceId', params: { sourceId: s.id } });
                  }}
                >
                  <td>
                    <div className="cell-main">
                      <span className="tool-icon">
                        <Icon name={SOURCE_TYPE[s.type].icon} />
                      </span>
                      <div>
                        <Link to="/conocimiento/$sourceId" params={{ sourceId: s.id }}>
                          {s.name}
                        </Link>
                        {s.status === 'syncing' ? (
                          <small>Sincronizando…</small>
                        ) : s.error ? (
                          <small className="error-text">Última sincronización con problemas</small>
                        ) : (
                          s.visibility === 'tenant' && <small>Compartida con todos los agentes</small>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{SOURCE_TYPE[s.type].label}</td>
                  <td className="num">
                    {s.readyCount === s.documentCount ? (
                      s.documentCount
                    ) : (
                      <span title={`${s.readyCount} listos de ${s.documentCount}`}>
                        {s.readyCount}/{s.documentCount}
                      </span>
                    )}
                  </td>
                  <td className="num nowrap" data-optional>
                    {fileSize(s.sizeBytes)}
                  </td>
                  <td className="muted nowrap" data-optional title={dateTime(s.updatedAt)}>
                    {relativeTime(s.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <NewSourceDialog open={creating} onOpenChange={setCreating} />
    </>
  );
}

/* ------------------------------------------------------------------ Detail */

function AddTextDialog({
  sourceId,
  open,
  onOpenChange,
}: {
  sourceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const add = useAddKnowledgeText(sourceId);
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const close = (next: boolean) => {
    if (!next) {
      setTitle('');
      setContent('');
      setErrors({});
    }
    onOpenChange(next);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = createTextDocumentSchema.safeParse({ title, content });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [i.path.join('.'), i.message])));
      return;
    }
    try {
      await add.mutateAsync(parsed.data);
      toast(`"${parsed.data.title}" agregado; lo estamos procesando`);
      close(false);
    } catch (err) {
      setErrors({ content: problemMessage(err, 'No se pudo guardar. Intenta de nuevo.') });
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={close}
      title="Agregar texto"
      description="Escribe como se lo explicarías a una persona nueva del equipo. Puedes usar títulos con #."
      submitLabel="Agregar texto"
      onSubmit={submit}
      loading={add.isPending}
      size="lg"
    >
      <Field label="Título" error={errors.title}>
        {(p) => (
          <input
            {...p}
            className="input"
            autoComplete="off"
            placeholder="Política de devoluciones"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setErrors(clearError('title'));
            }}
          />
        )}
      </Field>
      <Field
        label="Contenido"
        error={errors.content}
        help="No incluyas contraseñas ni datos de tarjetas: se ocultan automáticamente."
        foot={<span className="muted mono">{content.length.toLocaleString('es-CO')}/200.000</span>}
      >
        {(p) => (
          <textarea
            {...p}
            className="input"
            rows={12}
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              setErrors(clearError('content'));
            }}
          />
        )}
      </Field>
    </FormDialog>
  );
}

function UploadZone({ sourceId, ent }: { sourceId: string; ent: EntitlementsView | undefined }) {
  const upload = useUploadKnowledgeFile(sourceId);
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const allowed = ent?.values['knowledge.fileTypes'] ?? [];
  const maxMb = ent?.values['knowledge.maxFileMb'] ?? 0;
  const accept = allowed.map((t) => `.${t}`).join(',');

  const send = async (files: File[]) => {
    if (!files.length) return;
    setProgress({ done: 0, total: files.length });
    let ok = 0;
    for (const [i, file] of files.entries()) {
      const type = fileTypeFromName(file.name);
      if (!type || !allowed.includes(type)) {
        toast(`${file.name} no se cargó`, {
          tone: 'danger',
          detail: `Tu plan admite: ${allowed.map((t) => FILE_LABEL[t]).join(', ')}.`,
        });
      } else if (file.size > maxMb * 1024 * 1024) {
        toast(`${file.name} no se cargó`, { tone: 'danger', detail: `Supera el máximo de ${maxMb} MB.` });
      } else {
        try {
          await upload.mutateAsync(file);
          ok++;
        } catch (err) {
          toast(`${file.name} no se cargó`, {
            tone: 'danger',
            detail: problemMessage(err, 'Intenta de nuevo.'),
          });
        }
      }
      setProgress({ done: i + 1, total: files.length });
    }
    setProgress(null);
    if (ok)
      toast(
        ok === 1
          ? 'Archivo cargado; lo estamos procesando'
          : `${ok} archivos cargados; los estamos procesando`,
      );
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    void send([...e.dataTransfer.files]);
  };

  return (
    <div
      className="dropzone"
      data-dragging={dragging || undefined}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <Icon name="upload" size="lg" />
      {progress ? (
        <p aria-live="polite">
          Cargando {progress.done + 1 > progress.total ? progress.total : progress.done + 1} de{' '}
          {progress.total}…
        </p>
      ) : (
        <p>
          Arrastra archivos aquí o{' '}
          <Button variant="link" onClick={() => input.current?.click()}>
            elige desde tu equipo
          </Button>
        </p>
      )}
      <small className="muted">
        {allowed.map((t) => FILE_LABEL[t]).join(', ')} · hasta {maxMb} MB por archivo
      </small>
      <input
        ref={input}
        type="file"
        multiple
        accept={accept}
        hidden
        onChange={(e) => {
          void send([...(e.target.files ?? [])]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function UrlSettingsDialog({
  source,
  ent,
  open,
  onOpenChange,
}: {
  source: KnowledgeSource;
  ent: EntitlementsView | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateKnowledgeSource();
  const toast = useToast();
  const [maxPages, setMaxPages] = useState(String(source.url?.maxPages ?? 50));
  const [refresh, setRefresh] = useState<UrlRefresh>(source.url?.refresh ?? 'manual');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = urlSourceConfigSchema
      .pick({ maxPages: true, refresh: true })
      .safeParse({ maxPages: Number(maxPages), refresh });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [`url.${i.path.join('.')}`, i.message])));
      return;
    }
    try {
      await update.mutateAsync({ id: source.id, patch: { url: parsed.data } });
      toast('Configuración guardada', { detail: 'Se aplica en la próxima sincronización.' });
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiProblem) {
        const fields = err.fieldErrors();
        setErrors(
          Object.keys(fields).length ? fields : { 'url.maxPages': err.problem.detail ?? err.problem.title },
        );
      }
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Configurar sitio web"
      description={source.url?.startUrl}
      submitLabel="Guardar"
      onSubmit={submit}
      loading={update.isPending}
    >
      <UrlSettingsFields
        ent={ent}
        maxPages={maxPages}
        refresh={refresh}
        errors={errors}
        onChange={(p) => {
          if (p.maxPages !== undefined) setMaxPages(p.maxPages);
          if (p.refresh) setRefresh(p.refresh);
          setErrors({});
        }}
      />
    </FormDialog>
  );
}

function ApiSettingsDialog({
  source,
  ent,
  open,
  onOpenChange,
}: {
  source: KnowledgeSource;
  ent: EntitlementsView | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateKnowledgeSource();
  const toast = useToast();
  const [form, setForm] = useState<ApiForm>(() => apiFormFrom(source.api));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = apiSourceConfigSchema.safeParse(toConfig(form));
    if (!parsed.success) {
      setErrors(apiErrors(parsed.error.issues.map((i) => ({ ...i, path: ['api', ...i.path] }))));
      return;
    }
    try {
      await update.mutateAsync({
        id: source.id,
        patch: {
          api: parsed.data,
          ...(form.secret && form.authType !== 'none' ? { apiSecret: form.secret } : {}),
        },
      });
      toast('Conector actualizado', { detail: 'Se aplica en la próxima sincronización.' });
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiProblem) {
        const fields = err.fieldErrors();
        const issues = Object.entries(fields).map(([k, message]) => ({ path: k.split('.'), message }));
        setErrors(
          Object.keys(fields).length
            ? apiErrors(issues)
            : { endpoint: err.problem.detail ?? err.problem.title },
        );
      }
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Configurar conector"
      submitLabel="Guardar"
      onSubmit={submit}
      loading={update.isPending}
      size="lg"
    >
      <ApiConnectorFields
        form={form}
        errors={errors}
        ent={ent}
        hasStoredSecret={source.hasSecret}
        sourceId={source.id}
        onChange={(patch) => {
          setForm((f) => ({ ...f, ...patch }));
          setErrors({});
        }}
      />
    </FormDialog>
  );
}

function SyncStatus({ source }: { source: KnowledgeSource }) {
  if (source.status === 'syncing') {
    return (
      <Alert
        tone="info"
        icon="rotate"
        title={source.type === 'api' ? 'Leyendo la API…' : 'Leyendo el sitio…'}
        role="status"
      >
        Los elementos aparecen aquí a medida que se procesan. Puedes salir de esta pantalla.
      </Alert>
    );
  }
  if (source.error) {
    return (
      <Alert tone="warning" title="La última sincronización tuvo problemas">
        {source.error}
      </Alert>
    );
  }
  return null;
}

export function KnowledgeSourcePage() {
  const { sourceId } = sourceRoute.useParams();
  const source = useKnowledgeSource(sourceId);
  useDocumentTitle(source.data?.name ?? 'Conocimiento');
  const can = useCan();
  const ent = useEntitlements();
  const navigate = useNavigate();
  const toast = useToast();
  const retry = useRetryKnowledgeDocument();
  const removeDoc = useDeleteKnowledgeDocument();
  const removeSource = useDeleteKnowledgeSource();
  const sync = useSyncKnowledgeSource();
  const [configuring, setConfiguring] = useState(false);
  const [addingText, setAddingText] = useState(false);
  const [docTarget, setDocTarget] = useState<KnowledgeDocument | null>(null);
  const [deletingSource, setDeletingSource] = useState(false);
  const manage = can('knowledge.manage');

  const back = <BackLink to="/conocimiento">Conocimiento</BackLink>;
  if (source.isPending) {
    return (
      <>
        {back}
        <SkeletonRows rows={5} />
      </>
    );
  }
  if (source.isError) {
    return (
      <>
        {back}
        <ErrorState error={source.error} onRetry={() => void source.refetch()} />
      </>
    );
  }
  const s = source.data;
  const docs = s.documents;
  const synced = s.type === 'url' || s.type === 'api';

  const doRemoveDoc = async () => {
    if (!docTarget) return;
    try {
      await removeDoc.mutateAsync(docTarget.id);
      toast(`"${docTarget.title}" eliminado`);
    } catch (err) {
      toast('No se pudo eliminar', { tone: 'danger', detail: problemMessage(err, 'Inténtalo de nuevo.') });
    } finally {
      setDocTarget(null);
    }
  };

  const doSync = async () => {
    try {
      await sync.mutateAsync(s.id);
      toast('Sincronización iniciada');
    } catch (err) {
      toast('No se pudo sincronizar', { tone: 'danger', detail: problemMessage(err, 'Inténtalo de nuevo.') });
    }
  };

  const doRemoveSource = async () => {
    try {
      await removeSource.mutateAsync(s.id);
      toast(`Fuente "${s.name}" eliminada`);
      void navigate({ to: '/conocimiento' });
    } catch (err) {
      toast('No se pudo eliminar', { tone: 'danger', detail: problemMessage(err, 'Inténtalo de nuevo.') });
      setDeletingSource(false);
    }
  };

  return (
    <>
      <PageHeader
        back={back}
        lead={
          <span className="tool-icon">
            <Icon name={SOURCE_TYPE[s.type].icon} />
          </span>
        }
        title={s.name}
        description={SOURCE_TYPE[s.type].text}
        meta={
          <>
            <span className="chip">{SOURCE_TYPE[s.type].label}</span>
            {s.url && (
              <a href={s.url.startUrl} target="_blank" rel="noreferrer noopener" className="mono">
                {s.url.startUrl}
              </a>
            )}
            {s.api && <span className="mono muted">GET {s.api.endpoint}</span>}
            <span className="muted">
              {s.readyCount} de {s.documentCount} listos · {fileSize(s.sizeBytes)}
              {synced &&
                ` · ${s.lastSyncAt ? `sincronizado ${relativeTime(s.lastSyncAt)}` : 'sin sincronizar'}`}
              {synced && s.nextSyncAt && ` · próxima ${relativeTime(s.nextSyncAt)}`}
            </span>
          </>
        }
        actions={
          manage && (
            <>
              {s.type === 'text' && (
                <Button variant="primary" icon="plus" onClick={() => setAddingText(true)}>
                  Agregar texto
                </Button>
              )}
              {synced && (
                <Button
                  variant="primary"
                  icon="rotate"
                  loading={sync.isPending}
                  disabled={s.status === 'syncing'}
                  onClick={() => void doSync()}
                >
                  {s.status === 'syncing' ? 'Sincronizando…' : 'Sincronizar ahora'}
                </Button>
              )}
              <OverflowMenu
                label="Más acciones"
                actions={[
                  ...(synced
                    ? [
                        {
                          label: 'Configurar',
                          icon: 'sliders' as const,
                          onSelect: () => setConfiguring(true),
                        },
                      ]
                    : []),
                  {
                    label: 'Eliminar fuente',
                    icon: 'trash',
                    danger: true,
                    onSelect: () => setDeletingSource(true),
                  },
                ]}
              />
            </>
          )
        }
      />

      {manage && s.type === 'file' && <UploadZone sourceId={s.id} ent={ent.data} />}
      {synced && (
        <div className="page-alert">
          <SyncStatus source={s} />
        </div>
      )}

      {docs.length === 0 ? (
        <EmptyState icon={SOURCE_TYPE[s.type].icon} title="Esta fuente aún no tiene contenido" compact>
          {s.type === 'text'
            ? 'Agrega textos con la información que tus agentes deben conocer.'
            : synced
              ? 'Los elementos aparecerán aquí cuando termine la sincronización.'
              : 'Carga documentos para que tus agentes puedan consultarlos.'}
        </EmptyState>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Documento</th>
                <th scope="col">Estado</th>
                <th scope="col" className="num" data-optional>
                  Tamaño
                </th>
                <th scope="col" className="num" data-optional data-wide>
                  Fragmentos
                </th>
                <th scope="col" data-optional>
                  Actualizado
                </th>
                {manage && (
                  <th scope="col">
                    <span className="visually-hidden">Acciones</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id}>
                  <td>
                    <div className="cell-main">
                      <Icon
                        name={
                          d.fileType === 'text'
                            ? 'edit'
                            : d.fileType === 'web'
                              ? 'globe'
                              : d.fileType === 'api'
                                ? 'database'
                                : 'file'
                        }
                      />
                      <div>
                        <span className="name">{d.title}</span>
                        {d.error ? (
                          <small className="error-text">{d.error}</small>
                        ) : d.uri?.startsWith('api:') ? (
                          <small className="mono">ID {d.uri.slice(4)}</small>
                        ) : d.uri ? (
                          <small>
                            <a href={d.uri} target="_blank" rel="noreferrer noopener" className="mono">
                              {d.uri.replace(/^https:\/\//, '')}
                            </a>
                          </small>
                        ) : (
                          d.fileType !== 'text' && <small>{FILE_LABEL[d.fileType] ?? d.fileType}</small>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <DocumentStatusPill doc={d} />
                  </td>
                  <td className="num nowrap" data-optional>
                    {fileSize(d.sizeBytes)}
                  </td>
                  <td className="num" data-optional data-wide>
                    {d.status === 'ready' ? d.chunkCount : '—'}
                  </td>
                  <td className="muted nowrap" data-optional title={dateTime(d.updatedAt)}>
                    {relativeTime(d.updatedAt)}
                  </td>
                  {manage && (
                    <td className="actions-cell">
                      <OverflowMenu
                        size="sm"
                        label={`Acciones para ${d.title}`}
                        actions={[
                          ...(d.status === 'failed'
                            ? [
                                {
                                  label: 'Reintentar',
                                  icon: 'rotate' as const,
                                  onSelect: () =>
                                    void retry.mutateAsync(d.id).catch((err: unknown) =>
                                      toast('No se pudo reintentar', {
                                        tone: 'danger',
                                        detail: problemMessage(err, 'Inténtalo de nuevo.'),
                                      }),
                                    ),
                                },
                              ]
                            : []),
                          {
                            label: 'Eliminar',
                            icon: 'trash' as const,
                            danger: true,
                            onSelect: () => setDocTarget(d),
                          },
                        ]}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddTextDialog sourceId={s.id} open={addingText} onOpenChange={setAddingText} />
      {s.type === 'url' && configuring && (
        <UrlSettingsDialog source={s} ent={ent.data} open={configuring} onOpenChange={setConfiguring} />
      )}
      {s.type === 'api' && configuring && (
        <ApiSettingsDialog source={s} ent={ent.data} open={configuring} onOpenChange={setConfiguring} />
      )}
      <ConfirmDialog
        open={!!docTarget}
        onOpenChange={(o) => !o && setDocTarget(null)}
        title={`¿Eliminar "${docTarget?.title ?? ''}"?`}
        description="Los agentes dejarán de usarlo de inmediato y se liberará su espacio. Esta acción no se puede deshacer."
        confirmLabel="Eliminar documento"
        onConfirm={() => void doRemoveDoc()}
        loading={removeDoc.isPending}
      />
      <ConfirmDialog
        open={deletingSource}
        onOpenChange={setDeletingSource}
        title={`¿Eliminar la fuente "${s.name}"?`}
        description={`Se eliminarán sus ${s.documentCount} documentos y los agentes dejarán de usarla de inmediato. Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar fuente"
        onConfirm={() => void doRemoveSource()}
        loading={removeSource.isPending}
      />
    </>
  );
}
